import Dexie from "dexie";
import { isLockdownPersistenceActive } from "@client/offline/lockdownPersistenceControls";

/**
 * How many of the user's offline edits are still queued and unsynced.
 *
 * Sign-out deletes the queue, and those edits exist nowhere else until they
 * reach the server, so the flow has to be able to say what it is about to
 * destroy. Read from the durable queue directly: the count must cover every
 * round, and the sign-out page has no round connection to ask.
 */
export const PENDING_EDITS_DB = "SPAStorage";
export const PENDING_EDITS_TABLE = "unconfirmedActions2";
/** The queue's durable file under Lockdown. */
export const PENDING_EDITS_FILE = "spa-unconfirmed-actions";

export type PendingOfflineEdits = {
  /** Queued edits that would be destroyed by signing out. */
  edits: number;
  /** How many distinct rounds they belong to — the queue spans rounds. */
  rounds: number;
  /**
   * The queue could not be counted, so `edits` says nothing about what is at
   * risk. Callers must warn rather than purge: an unreadable queue may be a
   * full one.
   */
  unreadable: boolean;
};

/** Confidently nothing queued. */
const NONE: PendingOfflineEdits = { edits: 0, rounds: 0, unreadable: false };
/** There may be queued edits; we could not find out. */
const UNKNOWN: PendingOfflineEdits = { edits: 0, rounds: 0, unreadable: true };

export async function readPendingOfflineEdits(
  openDatabase: () => Promise<PendingEditsAccessor> = openPendingEditsTable,
  deps: {
    isLockdown?: () => boolean;
    readSnapshotFile?: (file: string) => Promise<string | null>;
    readSpareSnapshotFile?: (file: string) => Promise<string | null>;
  } = {},
): Promise<PendingOfflineEdits> {
  const isLockdown = deps.isLockdown ?? isLockdownPersistenceActive;
  // Under Lockdown the durable copy is a native file and the database is an
  // in-memory stand-in, hydrated only when the app opens it with its real
  // schema. Opening it from here creates it empty and latches it "hydrated",
  // so the round that opens later never loads the queue. Read the file.
  if (isLockdown()) {
    return readPendingFromSnapshot(
      deps.readSnapshotFile,
      deps.readSpareSnapshotFile,
    );
  }
  try {
    const table = await openDatabase();
    try {
      // Primary key is [aggregateId, actionId]: the keys alone give both
      // numbers without reading any action payload.
      const keys = await table.primaryKeys();
      const roundIds = new Set(
        keys.map((key) => (Array.isArray(key) ? String(key[0]) : String(key))),
      );
      return { edits: keys.length, rounds: roundIds.size, unreadable: false };
    } finally {
      table.close();
    }
  } catch (error) {
    // No database means nothing was ever queued — a confident zero. Any other
    // failure is not: reporting it as zero would purge the only copy without
    // a word.
    return isMissingDatabase(error) ? NONE : UNKNOWN;
  }
}

function isMissingDatabase(error: unknown): boolean {
  return error instanceof Error && error.name === Dexie.errnames.NoSuchDatabase;
}

/**
 * Count the queue from its durable file. The snapshot IO is imported lazily to
 * keep the Capacitor filesystem out of this page's static graph — this route
 * renders on web too.
 */
async function readPendingFromSnapshot(
  readFile?: (file: string) => Promise<string | null>,
  readSpareFile?: (file: string) => Promise<string | null>,
): Promise<PendingOfflineEdits> {
  try {
    const io = readFile == null ? await defaultSnapshotIO() : null;
    const read = readFile ?? ((file: string) => io!.read(file));
    // An injected snapshot reader with no spare reader means the caller is
    // modelling a device with no backup, not asking us to reach for the real
    // filesystem underneath its fake.
    const readSpare =
      readSpareFile ??
      (io == null ? async () => null : (file: string) => io.readSpare(file));

    // Same rule the engine hydrates by: the snapshot first, then the backup
    // taken while it was being replaced. Reading only the snapshot would
    // report a confident zero for a queue that is sitting in the backup, and
    // sign-out would purge both without a word.
    const raw = await read(PENDING_EDITS_FILE);
    const fromSnapshot = countRows(raw);
    if (fromSnapshot != null) return fromSnapshot;

    const spare = await readSpare(PENDING_EDITS_FILE);
    const fromSpare = countRows(spare);
    if (fromSpare != null) return fromSpare;

    // Nothing anywhere is a confident zero; anything else is damage.
    return raw == null && spare == null ? NONE : UNKNOWN;
  } catch {
    return UNKNOWN;
  }
}

/** The queue's numbers, or null when this copy holds nothing usable. */
function countRows(raw: string | null): PendingOfflineEdits | null {
  if (raw == null) return null;
  let rows: unknown;
  try {
    rows = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!Array.isArray(rows)) return null;
  const roundIds = new Set(
    rows
      .map((row) =>
        String((row as { aggregateId?: unknown })?.aggregateId ?? ""),
      )
      .filter((id) => id !== ""),
  );
  return { edits: rows.length, rounds: roundIds.size, unreadable: false };
}

async function defaultSnapshotIO() {
  const { createCapacitorSnapshotIO } =
    await import("@client/offline/capacitorSnapshotIO");
  return createCapacitorSnapshotIO();
}

type PendingEditsAccessor = {
  primaryKeys: () => Promise<unknown[]>;
  close: () => void;
};

type DbLike = {
  table: (name: string) => {
    toCollection: () => { primaryKeys: () => Promise<unknown[]> };
  };
  close: () => void;
};

/** Build the read surface over an already-open database. */
export function createPendingEditsAccessor(db: DbLike): PendingEditsAccessor {
  let table;
  try {
    table = db.table(PENDING_EDITS_TABLE);
  } catch (error) {
    // Throws on a database old enough to predate the store. The caller only
    // sees the error, so close here or the connection outlives the page.
    db.close();
    throw error;
  }
  return {
    primaryKeys: () => table.toCollection().primaryKeys(),
    close: () => db.close(),
  };
}

async function openPendingEditsTable(): Promise<PendingEditsAccessor> {
  // No stores declared, so Dexie reads the existing schema and this read-only
  // path can't trigger an upgrade.
  const db = new Dexie(PENDING_EDITS_DB);
  await db.open();
  return createPendingEditsAccessor(db as unknown as DbLike);
}

/** "1 unsynced change" / "2 unsynced changes" — used in title and body. */
export function describePendingEdits(count: number): string {
  return `${count} unsynced change${count === 1 ? "" : "s"}`;
}

/**
 * What to reopen. Deliberately unnamed: naming the round needs a second store
 * and the account stamp to prove the name belongs to the signed-in user, and
 * that machinery failed open in ways that disabled the warning itself.
 */
export function describeRoundsToReopen({
  rounds,
}: PendingOfflineEdits): string {
  return rounds === 1 ? "the round" : "the rounds";
}
